import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { Card, CardBody, CardHeader } from './Card.js'
import { Money } from './Money.js'
import { StatusPill } from './StatusPill.js'
import { Empty } from './Empty.js'
import { RecordPaymentDialog } from './RecordPaymentDialog.js'

/** One invoice row from GET /api/v1/fee-invoices?studentId=… — the fields this
 * panel needs. The endpoint returns more; we pick what we render. */
interface StudentInvoice {
  id: string
  invoiceNo: string
  studentName: string
  finalAmountInr: number
  amountPaidInr: number
  balanceInr: number
  dueDate: string
  status: 'pending' | 'partial' | 'paid' | 'overdue' | 'waived'
  paymentMethod: 'razorpay' | 'cash' | 'upi' | null
}

// Outstanding (money still owed) first, then settled. Within a group, newest due first.
const OUTSTANDING = new Set(['pending', 'partial', 'overdue'])

function fmtDate(d: string): string {
  return new Date(d).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * All of a student's fee invoices — custom AND plan-generated — with their paid
 * / balance state, so a partial payment is visible on the student page whatever
 * its source. Reuses the shared fee-invoices list endpoint and RecordPaymentDialog.
 */
export function StudentFeesPanel({ studentId }: { studentId: string }) {
  const qc = useQueryClient()
  const [payFor, setPayFor] = useState<StudentInvoice | null>(null)

  const invoicesQuery = useQuery({
    queryKey: ['student-fees', studentId],
    queryFn: async () => {
      const res = await api<StudentInvoice[]>(`/api/v1/fee-invoices?studentId=${studentId}`)
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const invoices = [...(invoicesQuery.data ?? [])].sort((a, b) => {
    const ao = OUTSTANDING.has(a.status) ? 0 : 1
    const bo = OUTSTANDING.has(b.status) ? 0 : 1
    if (ao !== bo) return ao - bo
    return b.dueDate.localeCompare(a.dueDate)
  })

  const outstandingPaise = invoices
    .filter((i) => OUTSTANDING.has(i.status))
    .reduce((s, i) => s + i.balanceInr, 0)
  const outstandingCount = invoices.filter((i) => OUTSTANDING.has(i.status)).length

  const description =
    invoices.length === 0
      ? undefined
      : outstandingCount === 0
        ? 'All settled.'
        : `₹${(outstandingPaise / 100).toLocaleString('en-IN')} outstanding across ${outstandingCount} invoice${outstandingCount === 1 ? '' : 's'}.`

  return (
    <Card>
      <CardHeader title="Fees" description={description} />
      <CardBody>
        {invoicesQuery.isLoading ? (
          <p className="text-sm text-fg-subtle">Loading…</p>
        ) : invoices.length === 0 ? (
          <Empty
            icon="wallet"
            title="No invoices yet"
            description="Invoices raised for this student will appear here."
          />
        ) : (
          <ul className="divide-y divide-border">
            {invoices.map((inv) => (
              <li key={inv.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                <span className="min-w-0">
                  <span className="flex items-center gap-2">
                    <span className="font-mono text-xs text-fg-subtle">{inv.invoiceNo}</span>
                    <StatusPill tone={inv.status}>{inv.status}</StatusPill>
                  </span>
                  <span className="mt-0.5 block text-xs text-fg-subtle">Due {fmtDate(inv.dueDate)}</span>
                </span>
                <span className="flex items-center gap-3 whitespace-nowrap">
                  <span className="text-right">
                    <Money paise={inv.finalAmountInr} variant="data" />
                    {inv.status === 'partial' ? (
                      <span className="block text-xs text-warning">
                        <Money paise={inv.balanceInr} variant="data" /> due
                      </span>
                    ) : null}
                  </span>
                  {OUTSTANDING.has(inv.status) ? (
                    <button
                      onClick={() => setPayFor(inv)}
                      className="text-xs text-fg-muted hover:text-success"
                    >
                      Record payment
                    </button>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardBody>

      <RecordPaymentDialog
        invoice={payFor}
        onClose={() => setPayFor(null)}
        onRecorded={() => {
          qc.invalidateQueries({ queryKey: ['student-fees', studentId] })
          // Keep the plan panel's per-installment balances in sync too.
          qc.invalidateQueries({ queryKey: ['fee-plan', studentId] })
        }}
      />
    </Card>
  )
}
